import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { COPYRIGHT_NOTICE } from "@/constants/legal";
import { Label } from "@/components/ui/label";
import { Cable, Loader2 } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-5">
      {/* Left visual */}
      <div className="relative hidden lg:col-span-3 lg:block">
        <img
          src="https://images.pexels.com/photos/2420212/pexels-photo-2420212.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Fiber"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-950/60" />
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="grid h-10 w-10 place-items-center rounded-sm bg-[#0052FF]">
              <Cable className="h-5 w-5" />
            </div>
            <span className="font-mono text-sm tracking-widest">EVOSOFT</span>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-300">{t("landing.tagline_short")}</div>
            <h1 className="mt-2 text-4xl font-bold leading-tight lg:text-5xl">
              {t("landing.hero_title")}
            </h1>
            <p className="mt-4 max-w-md text-slate-300">
              {t("landing.hero_subtitle")}
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="col-span-1 flex items-center justify-center bg-white px-6 py-12 lg:col-span-2">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="flex items-center justify-between">
            <div className="lg:hidden flex items-center gap-2">
              <div className="grid h-10 w-10 place-items-center rounded-sm bg-[#0052FF]">
                <Cable className="h-5 w-5 text-white" />
              </div>
              <span className="font-mono text-sm tracking-widest text-slate-900">EVOSOFT</span>
            </div>
            <div className="ml-auto"><LanguageSwitcher compact /></div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-slate-500">{t("auth.login")}</div>
            <h2 className="mt-1 text-3xl font-bold text-slate-900">{t("auth.welcome_back")}.</h2>
            <p className="mt-1 text-sm text-slate-500">{t("auth.sign_in_to_continue")}.</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">{t("common.email")}</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 rounded-sm"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">{t("common.password")}</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 rounded-sm"
                required
              />
            </div>
          </div>
          {error && (
            <div className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
              {error}
            </div>
          )}
          <Button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="w-full rounded-sm bg-[#0052FF] py-6 text-base font-semibold hover:bg-[#0040CC]"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.login")}
          </Button>
          <div className="text-center text-sm text-slate-500">
            {t("auth.no_account")}{" "}
            <a href="/signup" className="font-medium text-[#0052FF] hover:underline" data-testid="link-register">{t("landing.try_free_7_days")}</a>
          </div>
          <div className="pt-6 text-center text-[11px] text-slate-400" data-testid="login-copyright">{COPYRIGHT_NOTICE}</div>
        </form>
      </div>
    </div>
  );
}
